import { computed, defineComponent, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Editor from 'primevue/editor';

import { AppNewsRoutesEnum } from '../../router/AppNewsRoutesEnum';
import { AppNews } from '@/model/AppNews';
import { useMessage } from '@plugins/Messages-plugin';
import { appNewsService } from '@services/app-news.service';

import { PageHeader } from '@/components';

export default defineComponent({
  name: 'AppNewsDetailPage',

  components: {
    PageHeader,
    Editor,
  },

  props: {
    newsId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { t: $t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const { successMessage, errorMessage, confirmMessage } = useMessage();

    const news = ref<AppNews | null>(null);
    const submitted = ref(false);
    const imagePreview = ref<string | null>(null);
    const selectedImageFile = ref<File | null>(null);

    const isNew = computed(() => props.newsId === 'new');

    const publishedSwitch = computed({
      get: () => news.value?.published === 1,
      set: (val: boolean) => {
        if (news.value) {
          news.value.published = val ? 1 : 0;
        }
      },
    });

    const backRoute = computed(() => ({
      name: AppNewsRoutesEnum.APP_NEWS_LIST,
    }));

    const loadNews = async () => {
      if (isNew.value) {
        news.value = {
          title: '',
          content: '',
          short_description: '',
          published: 0,
        };
      } else {
        try {
          const response = await appNewsService.getById(Number(props.newsId));
          news.value = response;
        } catch (error) {
          errorMessage('Errore nel caricamento della news');
          console.error(error);
        }
      }
    };

    const onImageSelect = (event: any) => {
      const file = event.files[0];
      if (file) {
        selectedImageFile.value = file;
        
        // Crea anteprima
        const reader = new FileReader();
        reader.onload = (e: any) => {
          imagePreview.value = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    };

    const onSubmit = async () => {
      submitted.value = true;

      if (!news.value?.title || !news.value?.short_description || !news.value?.content) {
        errorMessage('Compila tutti i campi obbligatori');
        return;
      }

      try {
        let response;

        // Usa sempre FormData per gestire multipart/form-data
        const formData = new FormData();
        formData.append('title', news.value.title);
        formData.append('content', news.value.content);
        formData.append('short_description', news.value.short_description);
        formData.append('published', String(news.value.published));
        
        // Aggiungi l'immagine solo se è stata selezionata una nuova
        if (selectedImageFile.value) {
          formData.append('image', selectedImageFile.value);
        }

        if (news.value.id) {
          // Update
          response = await appNewsService.post<AppNews>(
            `${appNewsService['endPoint']}/${news.value.id}?_method=PATCH`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          successMessage('News aggiornata con successo');
        } else {
          // Create
          response = await appNewsService.post<AppNews>(
            appNewsService['endPoint'],
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          successMessage('News creata con successo');
          router.replace({
            name: AppNewsRoutesEnum.APP_NEWS_DETAIL,
            params: { newsId: response.id },
          });
        }

        news.value = response;
        submitted.value = false;
        imagePreview.value = null;
        selectedImageFile.value = null;
      } catch (error: any) {
        console.error(error);
        errorMessage(error?.response?.data?.message || 'Errore nel salvataggio della news');
      }
    };

    const onDelete = async () => {
      if (!news.value?.id) return;

      const confirmed = await confirmMessage('Sei sicuro di voler eliminare questa news?');
      if (!confirmed) return;

      try {
        await appNewsService.remove(news.value as AppNews);
        successMessage('News eliminata con successo');
        router.push({ name: AppNewsRoutesEnum.APP_NEWS_LIST });
      } catch (error) {
        errorMessage('Errore nell\'eliminazione della news');
        console.error(error);
      }
    };

    onMounted(() => {
      loadNews();
    });

    return {
      news,
      submitted,
      imagePreview,
      isNew,
      publishedSwitch,
      backRoute,
      onImageSelect,
      onSubmit,
      onDelete,
    };
  },
});
